'use client';

import { useLiveScoringSummary } from '@/hooks/useLiveStats';
import { optionalClockLabel, periodLabel } from '@/lib/liveStats';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { OtnScoringSummaryEntry } from '@models/ot_nationals/otn_live_stats';
import { Urls } from '@urls';
import Image from 'next/image';

type ScoringSummaryProps = {
	game: OtnGame;
	initialScores: OtnScoringSummaryEntry[];
	/** The page is a ?hidden=true preview, so the live re-fetch must ask for the hidden game too. */
	preview?: boolean;
};

/** Every score with its quarter, optional time and the running score. */
const ScoringSummary = ({ game, initialScores, preview = false }: ScoringSummaryProps) => {
	const scores = useLiveScoringSummary(game.id, initialScores, true, preview);
	if (!scores.length) return null;

	const teams = game.otn_games_otn_teams ?? [];
	const homeCode = teams.find((t) => t.is_home_team)?.otn_team?.team_code ?? 'HOME';
	const awayCode = teams.find((t) => !t.is_home_team)?.otn_team?.team_code ?? 'AWAY';

	return (
		<section className="otn-section-border flex flex-col gap-6 bg-black px-6 py-6" data-testid="scoring-summary">
			<div className="border-b-[2px] border-b-[#333] pb-6">
				<h2 className="otn-heading">Scoring Summary</h2>
			</div>
			<ol className="flex flex-col divide-y divide-[#333]">
				{scores.map((entry) => {
					const team = teams.find((t) => t.otn_team_id === entry.otn_team_id)?.otn_team;
					const clock = optionalClockLabel(entry.clock_ms);
					return (
						<li key={entry.play_id} className="flex items-center gap-3 py-3 text-[13px] leading-[150%]">
							<span className="text-otnBlue w-10 shrink-0 font-extrabold">{periodLabel(entry.period)}</span>
							{team?.logo_path && (
								<div className="relative h-7 w-7 shrink-0">
									<Image
										src={`${Urls.OvertimeImagesBaseUrl}${team.logo_path}`}
										fill
										alt={`${team.name || 'Team'} logo`}
									/>
								</div>
							)}
							<span className="min-w-0 flex-1">
								{entry.description}
								{clock && <span className="text-otnGray ml-2">{clock}</span>}
							</span>
							<span className="shrink-0 text-right font-extrabold whitespace-nowrap uppercase">
								{awayCode} {entry.away_score} – {homeCode} {entry.home_score}
							</span>
						</li>
					);
				})}
			</ol>
		</section>
	);
};

export default ScoringSummary;
